/**
 * Cache-root resolution + lazy legacy-dir migration primitives.
 *
 * `globalCacheRoot()` is the single root for all re-derivable global bytes
 * (`<root>/sources`, `<root>/catalog`). Sidecars are authored content and live
 * OUTSIDE this root (see `library/.../sidecar-paths.ts`).
 *
 * @docLink packages/core/api-reference#cache-paths
 */
import { existsSync, mkdirSync, readdirSync, renameSync, rmdirSync } from "node:fs";
import { homedir } from "node:os";
import { dirname, join } from "node:path";

/**
 * The Skaile home dir — root for user-scope, machine-global state
 * (`skaile.yaml` SSOT, `skaile.lock.yaml`, `cache/`, `config.yaml`).
 * `$SKAILE_HOME` wins (tests point it at a temp dir); else `~/.skaile`.
 */
export function globalSkaileDir(): string {
  return process.env.SKAILE_HOME ?? join(homedir(), ".skaile");
}

/**
 * The global cache root. `$SKAILE_CACHE_DIR` (now the cache *root*, not the
 * repos dir) wins; else `$SKAILE_HOME/cache`; else `~/.skaile/cache`.
 */
export function globalCacheRoot(): string {
  if (process.env.SKAILE_CACHE_DIR) return process.env.SKAILE_CACHE_DIR;
  return join(globalSkaileDir(), "cache");
}

const migratedWholeTree = new Set<string>();

/**
 * One-time whole-tree migration: if `newPath` is absent but `legacyPath` exists,
 * `rename()` legacy → new. Best-effort — a cross-device/raced rename is swallowed
 * (the caller's dir is re-derivable). Returns `newPath` always. Memoized per
 * `newPath` so hot resolvers don't stat on every call.
 */
export function migrateLegacyDir(newPath: string, legacyPath: string): string {
  if (migratedWholeTree.has(newPath)) return newPath;
  migratedWholeTree.add(newPath);
  try {
    if (!existsSync(newPath) && existsSync(legacyPath)) {
      mkdirSync(dirname(newPath), { recursive: true });
      renameSync(legacyPath, newPath);
    }
  } catch {
    // best-effort: re-derivable on miss
  }
  return newPath;
}

const mergedEntries = new Set<string>();

/**
 * Per-entry merge: move each immediate child of `legacyDir` that passes `filter`
 * into `targetDir/<name>`, skipping names already present in `targetDir`. Used
 * where two legacy roots feed one new root (so a whole-tree rename would clobber
 * or orphan). Removes `legacyDir` if it ends up empty. Idempotent; memoized per
 * `targetDir|legacyDir` pair.
 *
 * @param filter - receives the absolute child path; return true to move it.
 */
export function mergeLegacyEntriesInto(
  targetDir: string,
  legacyDir: string,
  filter: (childAbsPath: string) => boolean,
): void {
  const key = `${targetDir}\0${legacyDir}`;
  if (mergedEntries.has(key)) return;
  mergedEntries.add(key);
  if (!existsSync(legacyDir)) return;
  try {
    mkdirSync(targetDir, { recursive: true });
    for (const name of readdirSync(legacyDir)) {
      const from = join(legacyDir, name);
      if (!filter(from)) continue;
      const to = join(targetDir, name);
      if (existsSync(to)) continue; // don't clobber
      try {
        renameSync(from, to);
      } catch {
        // best-effort per entry
      }
    }
    if (readdirSync(legacyDir).length === 0) rmdirSync(legacyDir);
  } catch {
    // best-effort
  }
}

/**
 * Key-aware per-entry merge: like {@link mergeLegacyEntriesInto}, but each moved
 * child is placed at `targetDir/<remap(childAbsPath, name)>` instead of preserving
 * the bare `name`. Used to migrate legacy bare-name clones (`~/.skaile/sources/cli`)
 * into the URL-keyed cache layout (`<cacheRoot>/sources/<host>/<owner>/<repo>`) so a
 * later URL-keyed lookup finds them. `remap` returning `null`/`undefined` (or a
 * value equal to `name`) falls back to preserving the bare name.
 *
 * Nested target keys (`host/owner/repo`) create intermediate dirs. Skips a child
 * when its computed target already exists (don't clobber). Removes `legacyDir`
 * if it empties. Idempotent; memoized per `targetDir|legacyDir` pair.
 *
 * @param filter - receives the absolute child path; return true to consider it.
 * @param remap  - receives `(childAbsPath, name)`; returns the target key (a
 *                 possibly-nested relative path) or a falsy value to keep `name`.
 */
export function mergeLegacyEntriesKeyed(
  targetDir: string,
  legacyDir: string,
  filter: (childAbsPath: string) => boolean,
  remap: (childAbsPath: string, name: string) => string | null | undefined,
): void {
  const key = `${targetDir}\0${legacyDir}`;
  if (mergedEntries.has(key)) return;
  mergedEntries.add(key);
  if (!existsSync(legacyDir)) return;
  try {
    mkdirSync(targetDir, { recursive: true });
    for (const name of readdirSync(legacyDir)) {
      const from = join(legacyDir, name);
      if (!filter(from)) continue;
      const targetKey = remap(from, name) || name;
      const to = join(targetDir, targetKey);
      if (existsSync(to)) continue; // don't clobber
      try {
        mkdirSync(dirname(to), { recursive: true });
        renameSync(from, to);
      } catch {
        // best-effort per entry
      }
    }
    if (readdirSync(legacyDir).length === 0) rmdirSync(legacyDir);
  } catch {
    // best-effort
  }
}

/** Test-only: reset migration memoization between cases. */
export function __resetCacheMigrationMemo(): void {
  migratedWholeTree.clear();
  mergedEntries.clear();
}
